import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';

import { maybeAddChangeListener } from 'stores/DynamicProperties/changeListeners';
import { type IDynamicProperty } from 'stores/DynamicProperties/IDynamicProperty';
import DynamicGroup from 'stores/DynamicProperties/Types/Group';
import { DynamicPropertyType } from 'types/DynamicPropertyType';
import { useForceUpdate } from 'utils/Hooks';
import { camelCaseToTitle } from 'utils/String';

import PropertyPopover from '../PropertyPopover';
import AssetInput from './AssetInput';
import InputGroup from './InputGroup';
import MultiLineTextInput from './MultiLineTextInput';
import SelectInput from './SelectInput';
import SVGTextInput from './SVGTextInput';
import SwitchInput from './SwitchInput';
import TemplateInput from './TemplateInput';
import TextInput from './TextInput';

// Input hints are used to provide additional information about how an input
// should be rendered. Currently, these only apply to input components
// rendered for a DynamicPropertyType.TEXT type.

// No hint; same as omitted
export interface DefaultInputHint {
    variant: 'default';
}

// keep one for our own use when the inputHint is omitted
const defaultInputHint: DefaultInputHint = { variant: 'default' };

// Render a <textarea> instead of an <input type="text">
export interface MultilineInputHint {
    variant: 'multiline';
}

// Render select UI with the given strings as options, not free text.
export interface EnumConstraintHint {
    variant: 'enum';
    values: ReadonlyArray<string>;
}

export type InputHint =
    | DefaultInputHint
    | MultilineInputHint
    | EnumConstraintHint;

export type InputContainerSize = 'full' | 'half' | 'third' | 'quarter';

const sizeWidths: Record<InputContainerSize, string> = {
    full: '100%',
    half: '49.5%',
    third: '32.2%',
    quarter: '24.5%',
};

interface ContainerProps {
    $changed?: boolean;
    $size?: InputContainerSize;
}

const Container = styled.div<ContainerProps>`
    display: flex;
    align-items: center;
    margin-bottom: 7px;

    /*
     * Both selectors, because this container wraps either kind of control:
     * TextInput renders an Input, while TemplateInput, AssetInput and
     * SelectInput render a Select. Unlike ScalarContainer, which only ever
     * styles TextInput, this rule has to reach both.
     */
    ${(p) =>
        p.$changed &&
        css`
            .ant-input,
            .ant-select-selector {
                background: #e1f3ff !important;
            }
        `}

    ${(p) =>
        p.$size &&
        css`
            width: ${sizeWidths[p.$size]};
        `}
`;

const PropertyLabel = styled.div`
    width: 115px;
    font-weight: bold;
    font-size: 13px;
    line-height: 15px;
    margin-right: 10px;
`;

const BeforeLabel = styled.div`
    font-size: 12px;
    font-weight: 500;
    padding: 0 11px;
    background: #ffffff;
    border: 1px solid #d6d6d6;
    height: 32px;
    margin-right: -3px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

function isGroupInputType(property: IDynamicProperty<any>): boolean {
    // Note that it's not strictly necessary for a group-like property to
    // derive from `DynamicGroup`, it just happens to be the case.
    return property instanceof DynamicGroup;
}

interface ComponentForPropertyProps {
    property: IDynamicProperty<any>;
    disabled: boolean;
    editMode: boolean;
    inputHint: InputHint;
}

function ComponentForProperty({
    property,
    disabled,
    editMode,
    inputHint,
}: ComponentForPropertyProps) {
    const logPrefix = `[InputContainer#ComponentForProperty]`;
    if (
        inputHint.variant !== 'default' &&
        property.type !== DynamicPropertyType.TEXT
    ) {
        console.warn(
            logPrefix +
                ` Input hint ${JSON.stringify(inputHint.variant)}` +
                ` is not applicable to property type "${property.type}"` +
                ` (name="${property.name}")`
        );
    }

    switch (property.type) {
        // Group-like property types
        case DynamicPropertyType.DIMENSIONS:
        case DynamicPropertyType.GROUP:
            return (
                <InputGroup
                    disabled={disabled}
                    editMode={editMode}
                    property={property as DynamicGroup}
                />
            );

        // Text-like property types, presented at full width
        case DynamicPropertyType.LABEL:
            return (
                <TextInput
                    disabled={disabled}
                    size="full"
                    property={property}
                />
            );

        // Pure text is special, as it can act on the inputHint.
        case DynamicPropertyType.TEXT: {
            // Bound once so the unhandled-variant fallback below can reuse it
            // rather than repeating the element.
            const plainTextInput = (
                <TextInput
                    disabled={disabled}
                    size="full"
                    property={property}
                />
            );

            switch (inputHint.variant) {
                case 'default':
                    return plainTextInput;

                case 'multiline':
                    return (
                        <MultiLineTextInput
                            property={property}
                            disabled={disabled}
                        />
                    );

                case 'enum':
                    return (
                        <SelectInput
                            property={property}
                            options={inputHint.values}
                            disabled={disabled}
                        />
                    );

                default: {
                    // At the time of writing, this should be unreachable
                    // (provably so by TypeScript), but in case other
                    // InputHints are ever invented, this will warn and fall
                    // back if a handler is forgotten about.

                    // Note that this looks like an unnecessary alias, but
                    // annotating the :never type ensures that tsc will
                    // report an error if the above cases are not exhaustive.
                    const unhandledHint: never = inputHint;

                    console.warn(
                        logPrefix +
                            ` Unhandled input hint ${JSON.stringify(unhandledHint)}` +
                            ` for property "${property.name}"`
                    );
                    return plainTextInput;
                }
            }
        }

        // Text-like property types, presented compactly
        case DynamicPropertyType.COLOR:
        case DynamicPropertyType.NUMBER:
        case DynamicPropertyType.PERCENTAGE:
        case DynamicPropertyType.PIXELS:
            return (
                <TextInput
                    disabled={disabled}
                    size="small"
                    property={property}
                />
            );

        // Bespoke or one-offs
        case DynamicPropertyType.SVGText:
            return (
                <SVGTextInput
                    disabled={disabled}
                    editMode={editMode}
                    property={property as DynamicGroup}
                />
            );

        case DynamicPropertyType.ASSET:
            return <AssetInput disabled={disabled} property={property} />;

        case DynamicPropertyType.BOOLEAN:
            return <SwitchInput disabled={disabled} property={property} />;

        case DynamicPropertyType.TEMPLATE:
            return <TemplateInput disabled={disabled} property={property} />;

        default:
            // If a DynamicPropertyType is not mentioned above it shouldn't
            // be rendered, unless a component rendering for it has been
            // forgotten. But at the time of writing this excludes:
            //
            // - DynamicPropertyType.SVGData
            //     Internal value used by SVGText. Stores the memoized SVG
            //     document rendered by glyphy. Hidden from the user.
            //
            // We log a warning if we get to this point because none of the
            // above types should make it this far.
            {
                console.warn(
                    `${logPrefix} No render for property type "${property.type}"` +
                        ` (name="${property.name}")`
                );
            }
            return null;
    }
}

export interface InputContainerProps {
    addonBefore?: string;
    disabled?: boolean;
    editMode?: boolean;
    inputHint?: InputHint;
    property: IDynamicProperty<any> | undefined;
    size?: InputContainerSize;
}

function InputContainer({
    addonBefore,
    disabled = false,
    editMode = false,
    inputHint = defaultInputHint,
    property,
    size,
}: InputContainerProps) {
    const forceUpdate = useForceUpdate();
    useEffect(
        () => maybeAddChangeListener(property, forceUpdate),
        [property, forceUpdate]
    );

    if (!property) return null;

    const isGroup = isGroupInputType(property);
    const showLabel = property.name && !isGroup;
    const markAsChanged = !isGroup && property.getChanged();

    const content = (
        <ComponentForProperty
            property={property}
            disabled={disabled}
            editMode={editMode}
            inputHint={inputHint}
        />
    );

    return (
        <Container $size={size} $changed={markAsChanged}>
            {showLabel && (
                <PropertyPopover property={property} editMode={editMode}>
                    <PropertyLabel>
                        {camelCaseToTitle(property.name)}
                    </PropertyLabel>
                </PropertyPopover>
            )}

            {addonBefore && <BeforeLabel>{addonBefore}</BeforeLabel>}
            {content}
        </Container>
    );
}

export default observer(InputContainer);
